import { Box, Link } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import Markdown from 'markdown-to-jsx';
import React from 'react';

import SlideLinkWithPreview from 'components/atoms/SlideLinkWithPreview';
import { useEntityMergeTagResolver } from 'utils/useEntityMergeTagResolver';

const descriptionMarkdownOptions = {
  overrides: {
    a: {
      component: Link,
      props: { target: '_blank', rel: 'noopener noreferrer', underline: 'hover' },
    },
    'slide-link': { component: SlideLinkWithPreview },
  },
};

const descriptionMarkdownSx: SxProps<Theme> = {
  '& p': { margin: 0, '&:not(:last-child)': { mb: 0.5 } },
  '& ul, & ol': { mt: 0.25, mb: 0.5, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& code': { fontSize: '0.75rem', bgcolor: 'action.hover', px: 0.5, py: 0.25, borderRadius: 0.5 },
  '& pre': {
    bgcolor: 'action.hover',
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    '& code': { bgcolor: 'transparent', p: 0 },
  },
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
  },
  fontSize: '0.75rem',
  lineHeight: 1.5,
  color: 'text.secondary',
};

interface Props {
  content: string;
  studyId?: string;
  sx?: SxProps<Theme>;
}

const StudyReportDescriptionMarkdown: React.FC<Props> = ({ content, studyId, sx }) => {
  const resolvedContent = useEntityMergeTagResolver({ content, studyId });
  const mergedSx: SxProps<Theme> = React.useMemo(() => {
    if (!sx) {
      return descriptionMarkdownSx;
    }

    return Array.isArray(sx) ? [descriptionMarkdownSx, ...sx] : [descriptionMarkdownSx, sx];
  }, [sx]);

  return (
    <Box sx={mergedSx}>
      <Markdown options={descriptionMarkdownOptions}>{resolvedContent}</Markdown>
    </Box>
  );
};

export default StudyReportDescriptionMarkdown;
